import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  productionUiDisplayStateSelector,
  resolveUnmatchedBookingsDisplay,
} from "@ryde/shared";

export const useProductionPreviewStatistics = () => {
  const {
    totalBookingCount,
    allInputBookingIds,
    bookingsAlreadyInProduceCount,
    bookingsAlreadyInProduceIds,
    joinedRideCount,
    joinedBookingCount,
    producedRideCount,
    failedToSaveJoinedBookingCount,
    failedToSaveJoinedBookingIds,
    failedToSaveJoinedRideCount,
    failedToSaveJoinedRideIds,
    failedToSaveProducedRidesCount,
    producedBookingCount,
    producedBookingIds,
    producedAndJoinedRideIds,
    producedSavedRideIds,
    joinedRideIds,
    rideTooShortBookingCount,
    rideTooShortBookingIds,
    notFoundRidesToJoinBookingCount,
    notFoundRidesToJoinBookingIds,
    joinedBookingIds,
    joinedSentRideSupplierCanceledIds,
    joinedSentRideSupplierCanceledCount,
    joinedSentRideSupplierUpdatedIds,
    joinedSentRideSupplierUpdatedCount,
    courseJoinedSentRideSupplierCanceledIds,
    courseJoinedSentRideSupplierCanceledCount,
    courseJoinedSentRideSupplierUpdatedIds,
    courseJoinedSentRideSupplierUpdatedCount,
    coursesSkippedOnNoPassengersCount,
    coursesSkippedOnNoPassengers,
    coursesAlreadyExistCount,
    coursesAlreadyExist,
    producedCoursesCount,
    producedCourses,
    notSavedCoursesCount,
    notSavedCourses,
    skippedPassengerBookingCount,
    skippedPassengerBookingIds,
    hubBookingIds,
    hubBookingCount,
    nonHubBookingCount,
    hubMainRideIds,
    hubMainRideCount,
    hubPersonalRideIds,
    hubPersonalRideCount,
    hubExpectedMainRideCount,
    hubExpectedPersonalRideCount,
    hubBookingsSkippedInSplitIds,
    hubBookingsSkippedInSplitCount,
    hubPersonalGeoFailedBookingIds,
    hubPersonalGeoFailedCount,
    hubBookingsWithMainSegmentIds,
    hubBookingsWithMainSegmentCount,
    hubIncompletePairingBookingIds,
    hubIncompletePairingCount,
    hubBookingsProducedIds,
    hubBookingsProducedCount,
    hubCompanionBookingIds,
    hubCompanionBookingCount,
    hubBookingsExcludedFromJoinIds,
    hubExplicitSkipBookingIds,
    hubExplicitSkipBookingCount,
  } = useRecoilValue(productionUiDisplayStateSelector);

  const producedRideIdsComputed = useMemo(() => {
    if (!producedAndJoinedRideIds?.length) {
      return [];
    }

    const joinedRideIdsSet = new Set(joinedRideIds || []);
    return producedAndJoinedRideIds.filter(id => !joinedRideIdsSet.has(id));
  }, [producedAndJoinedRideIds, joinedRideIds]);
  const producedRideIds = producedSavedRideIds ?? producedRideIdsComputed;

  const {
    hasSkippedPassengersMetric,
    unmatchedBookingsDisplayCount,
    unmatchedBookingsDisplayIds,
    unmatchedBookingsLabelKey,
  } = useMemo(
    () =>
      resolveUnmatchedBookingsDisplay({
        totalBookingCount,
        failedToSaveJoinedBookingCount,
        joinedBookingCount,
        producedBookingCount,
        rideTooShortBookingCount,
        notFoundRidesToJoinBookingCount,
        skippedPassengerBookingCount,
        allInputBookingIds,
        joinedBookingIds,
        producedBookingIds,
        rideTooShortBookingIds,
        notFoundRidesToJoinBookingIds,
        failedToSaveJoinedBookingIds,
        skippedPassengerBookingIds,
      }),
    [
      totalBookingCount,
      failedToSaveJoinedBookingCount,
      joinedBookingCount,
      producedBookingCount,
      rideTooShortBookingCount,
      notFoundRidesToJoinBookingCount,
      skippedPassengerBookingCount,
      allInputBookingIds,
      joinedBookingIds,
      producedBookingIds,
      rideTooShortBookingIds,
      notFoundRidesToJoinBookingIds,
      failedToSaveJoinedBookingIds,
      skippedPassengerBookingIds,
    ],
  );
  const savedRidesCount = producedAndJoinedRideIds?.length || 0;
  const producedButNotSavedRidesCount = failedToSaveProducedRidesCount;

  return {
    totalBookingCount,
    allInputBookingIds,
    bookingsAlreadyInProduceCount,
    bookingsAlreadyInProduceIds,
    joinedRideCount,
    joinedBookingCount,
    producedRideCount,
    producedRideIds,
    failedToSaveJoinedBookingCount,
    failedToSaveJoinedBookingIds,
    failedToSaveJoinedRideCount,
    failedToSaveJoinedRideIds,
    producedBookingCount,
    producedBookingIds,
    rideTooShortBookingCount,
    rideTooShortBookingIds,
    joinedBookingIds,
    joinedRideIds,
    producedAndJoinedRideIds,
    joinedSentRideSupplierCanceledIds,
    joinedSentRideSupplierCanceledCount,
    joinedSentRideSupplierUpdatedIds,
    joinedSentRideSupplierUpdatedCount,
    courseJoinedSentRideSupplierCanceledIds,
    courseJoinedSentRideSupplierCanceledCount,
    courseJoinedSentRideSupplierUpdatedIds,
    courseJoinedSentRideSupplierUpdatedCount,
    coursesSkippedOnNoPassengersCount,
    coursesSkippedOnNoPassengers,
    coursesAlreadyExistCount,
    coursesAlreadyExist,
    producedCoursesCount,
    producedCourses,
    notSavedCoursesCount,
    notSavedCourses,
    hasSkippedPassengersMetric,
    unmatchedBookingsDisplayCount,
    unmatchedBookingsDisplayIds,
    unmatchedBookingsLabelKey,
    savedRidesCount,
    producedButNotSavedRidesCount,
    hubBookingIds,
    hubBookingCount,
    nonHubBookingCount,
    hubMainRideIds,
    hubMainRideCount,
    hubPersonalRideIds,
    hubPersonalRideCount,
    hubExpectedMainRideCount,
    hubExpectedPersonalRideCount,
    hubBookingsSkippedInSplitIds,
    hubBookingsSkippedInSplitCount,
    hubPersonalGeoFailedBookingIds,
    hubPersonalGeoFailedCount,
    hubBookingsWithMainSegmentIds,
    hubBookingsWithMainSegmentCount,
    hubIncompletePairingBookingIds,
    hubIncompletePairingCount,
    hubBookingsProducedIds,
    hubBookingsProducedCount,
    hubCompanionBookingIds,
    hubCompanionBookingCount,
    hubBookingsExcludedFromJoinIds,
    hubExplicitSkipBookingIds,
    hubExplicitSkipBookingCount,
  };
};
